import type { NotificationItem } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  cn,
  EmptyState,
  Label,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BellOff, CheckCheck, ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { type ReactElement, useId } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  digestFromNotification,
  markAllBefore,
  parseAppLink,
} from "../../../features/alarms/alarm-values.js";
import { DeliveryLog } from "../../../features/alarms/deliveries.js";
import { DigestView } from "../../../features/alarms/digest-view.js";
import { NotificationBadge } from "../../../features/alarms/notification-bell.js";
import {
  useNotifications,
  useRefreshNotifications,
  useUnreadCount,
} from "../../../features/alarms/queries.js";
import { AppLinkTo, NotificationsPage, RelativeTime } from "../../../features/alarms/shared.js";
import { TestNotificationButton } from "../../../features/alarms/test-notification.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

const TABS = ["inbox", "deliveries"] as const;
const searchSchema = z.object({
  tab: z.enum(TABS).optional().catch(undefined),
  unread: z
    .union([z.literal(true), z.literal("true")])
    .transform(() => true as const)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/_app/bildirimler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: NotificationCenterRoute,
});

const PAGE_SIZE = 20;

/**
 * Bildirim merkezi (Prompt 20 web): kişisel bildirim kutusu (cursor sayfalı, "yalnızca okunmamışlar", okundu / tümü
 * okundu; özet bildirimleri yapısıyla bölüm bölüm), teslim günlüğü ("bildirim neden gelmedi") ve test bildirimi.
 * `notifications.notification.created` olayı listeyi canlı tazeler. İzin istemez (yalnızca `ALERTS` bayrağı).
 */
function NotificationCenterRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const tab = search.tab ?? "inbox";
  return (
    <NotificationsPage
      title={t("menu.notifications")}
      description={t("notifications.center.description")}
      actions={
        <>
          <TestNotificationButton />
          <Button asChild variant="ghost">
            <Link to="/bildirimler/tercihler">
              <SlidersHorizontal aria-hidden="true" />
              {t("menu.notificationPreferences")}
            </Link>
          </Button>
        </>
      }
    >
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            to: ".",
            search: ((previous: Record<string, unknown>) => ({
              ...previous,
              tab: value === "inbox" ? undefined : value,
            })) as never,
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("notifications.center.tabs")}>
          <TabsTrigger value="inbox">{t("notifications.center.inbox")}</TabsTrigger>
          <TabsTrigger value="deliveries">{t("notifications.center.deliveries")}</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox">
          <Inbox unreadOnly={search.unread === true} />
        </TabsContent>
        <TabsContent value="deliveries">
          <DeliveryLog />
        </TabsContent>
      </Tabs>
    </NotificationsPage>
  );
}

function Inbox({ unreadOnly }: { unreadOnly: boolean }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const unreadId = useId();
  const refresh = useRefreshNotifications();
  const pages = useCursorPages(String(unreadOnly));
  const list = useNotifications({ unreadOnly, cursor: pages.cursor, limit: PAGE_SIZE });
  const unread = useUnreadCount();
  const items = list.data?.items ?? [];

  const markRead = useMutation({
    mutationFn: async (id: string) => api.POST("/notifications/{id}/read", { params: { path: { id } } }),
    onSuccess: () => refresh(),
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });
  const markAll = useMutation({
    mutationFn: async () => {
      // İlk sayfadayken görünen en yeni bildirime kadar (sonradan gelen okunmamış kalır); diğer sayfalarda tümü.
      const before = pages.pageNumber === 1 ? markAllBefore(items) : undefined;
      return (await api.POST("/notifications/read-all", { body: before ? { before } : {} })).data!;
    },
    onSuccess: async (result) => {
      await refresh();
      toast({ tone: "success", title: t("notifications.center.markedAll", { count: result.updated }) });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id={unreadId}
            checked={unreadOnly}
            onChange={(event) =>
              void navigate({
                to: ".",
                search: ((previous: Record<string, unknown>) => ({
                  ...previous,
                  unread: event.target.checked ? true : undefined,
                })) as never,
                replace: true,
              })
            }
          />
          <Label htmlFor={unreadId} className="font-normal">
            {t("notifications.center.unreadOnly")}
          </Label>
          {unread.data ? (
            <span className="text-sm text-muted-foreground">
              {t("notifications.center.unreadCount", { count: unread.data.unread })}
            </span>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={(unread.data?.unread ?? 0) === 0}
          loading={markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          <CheckCheck aria-hidden="true" />
          {t("notifications.markAllRead")}
        </Button>
      </div>
      {list.error ? (
        <Alert tone="danger">{errorMessage(list.error)}</Alert>
      ) : list.isPending ? (
        <SkeletonRows rows={5} />
      ) : items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<BellOff />}
          title={
            unreadOnly ? t("notifications.center.emptyUnreadTitle") : t("notifications.center.emptyTitle")
          }
          description={t("notifications.center.emptyDescription")}
        />
      ) : (
        <ul className="grid gap-3" aria-label={t("notifications.center.listLabel")}>
          {items.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkRead={() => markRead.mutate(notification.id)}
              marking={markRead.isPending && markRead.variables === notification.id}
            />
          ))}
        </ul>
      )}
      {pages.hasPrevious || list.data?.page.hasMore ? (
        <nav
          className="flex items-center justify-end gap-2"
          aria-label={t("notifications.center.pagination")}
        >
          <span className="mr-2 text-sm text-muted-foreground">
            {t("common.pageNumber", { page: pages.pageNumber })}
          </span>
          <Button variant="outline" size="sm" onClick={pages.previous} disabled={!pages.hasPrevious}>
            <ChevronLeft aria-hidden="true" />
            {t("notifications.center.previous")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => pages.next(list.data?.page.nextCursor)}
            disabled={!list.data?.page.hasMore}
          >
            {t("notifications.center.next")}
            <ChevronRight aria-hidden="true" />
          </Button>
        </nav>
      ) : null}
    </div>
  );
}

function NotificationCard({
  notification,
  onMarkRead,
  marking,
}: {
  notification: NotificationItem;
  onMarkRead: () => void;
  marking: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const isUnread = !notification.readAt;
  const target = parseAppLink(notification.link);
  const digest = digestFromNotification(notification);
  return (
    <li>
      <Card className={cn("grid gap-2 p-4", isUnread && "border-primary/60")}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <NotificationBadge notification={notification} />
          {notification.stationCode ? (
            <span className="text-sm text-muted-foreground">{notification.stationCode}</span>
          ) : null}
          <span className="text-sm text-muted-foreground" title={formatDateTime(notification.createdAt)}>
            <RelativeTime value={notification.createdAt} />
          </span>
          {isUnread ? <StatusBadge tone="info">{t("notifications.unreadBadge")}</StatusBadge> : null}
        </div>
        <h2 className={cn("text-sm", isUnread ? "font-semibold" : "font-medium")}>{notification.title}</h2>
        {digest ? (
          <DigestView digest={digest} />
        ) : (
          <p className="whitespace-pre-line text-sm text-muted-foreground">{notification.body}</p>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {target ? (
            <Button asChild variant="outline" size="sm">
              <AppLinkTo link={target} {...(isUnread ? { onClick: onMarkRead } : {})}>
                {notification.kind === "ALARM_GROUP"
                  ? t("notifications.center.openGroup")
                  : notification.kind === "ALARM"
                    ? t("notifications.center.openAlarm")
                    : t("notifications.center.open")}
              </AppLinkTo>
            </Button>
          ) : null}
          {isUnread ? (
            <Button variant="ghost" size="sm" onClick={onMarkRead} loading={marking}>
              {t("notifications.center.markRead")}
            </Button>
          ) : null}
        </div>
      </Card>
    </li>
  );
}
